import { create } from 'zustand';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCY_SYMBOLS = {
  INR: '₹', USD: '$', GBP: '£', EUR: '€',
  SGD: 'S$', AED: 'AED', AUD: 'A$', CAD: 'C$', NZD: 'NZ$',
};

const CURRENCY_LOCALES = {
  INR: 'en-IN', USD: 'en-US', GBP: 'en-GB', EUR: 'de-DE',
  SGD: 'en-SG', AED: 'ar-AE', AUD: 'en-AU', CAD: 'en-CA', NZD: 'en-NZ',
};

const STORAGE_KEY = 'gn_currency';
const FRESH_MS = 12 * 60 * 60 * 1000; // 12h — currency is stable during a session

const cached = (() => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
})();

export const useCurrencyStore = create((set, get) => ({
  currency: cached.currency || 'INR',
  currencySymbol: cached.currency_symbol || '₹',
  region: cached.region || 'IN',
  paymentSupported: (cached.currency || 'INR') === 'INR',
  loading: !cached.currency,
  detected: !!cached.currency,

  // Idempotent: geolocation round-trip runs at most once per freshness window.
  // Guarded by `_detecting` so concurrent mounts don't fan out duplicate calls.
  _detecting: false,
  detect: async () => {
    const s = get();
    if (s._detecting) return;
    if (cached.currency && cached.ts && Date.now() - cached.ts < FRESH_MS) {
      set({ loading: false, detected: true });
      return;
    }
    set({ _detecting: true });
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/geolocation/detect`, { withCredentials: true });
      if (data) {
        const cur = data.currency || 'INR';
        set({
          currency: cur,
          currencySymbol: data.currency_symbol || CURRENCY_SYMBOLS[cur] || '₹',
          region: data.region || 'IN',
          paymentSupported: cur === 'INR', // foreign-currency charging not wired yet
        });
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          currency: cur, currency_symbol: data.currency_symbol, region: data.region, ts: Date.now(),
        }));
      }
    } catch {
      // Keep INR default on failure
    } finally {
      set({ loading: false, detected: true, _detecting: false });
    }
  },
}));

export const formatPriceIn = (currency, amount, showSymbol = true) => {
  if (!amount && amount !== 0) return '';
  const locale = CURRENCY_LOCALES[currency] || 'en-US';
  return new Intl.NumberFormat(locale, {
    style: showSymbol ? 'currency' : 'decimal',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};
